import React, { useState, useEffect } from 'react'
import { Routes, Route, Link, Navigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useInstitution } from '../../contexts/InstitutionContext'
import { api } from '../../services/api'
import './AdminApp.css'

interface FirebaseUser {
  firebase_uid: string;
  email: string;
  display_name?: string;
  role: string;
  institution_id?: string;
  created_at: string;
}

const AdminApp = () => {
  const { currentUser, logout } = useAuth()

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error('Error logging out:', error)
    }
  }

  return (
    <div className="admin-platform">
      <header className="admin-header">
        <div className="admin-nav">
          <h1>Maxel Super Admin</h1>
          <nav className="admin-navigation">
            <Link to="/admin/dashboard" className="nav-link">Dashboard</Link>
            <Link to="/admin/institutions" className="nav-link">Institutions</Link>
            <Link to="/admin/licenses" className="nav-link">Licenses</Link>
            <Link to="/admin/users" className="nav-link">Users</Link>
            <Link to="/admin/analytics" className="nav-link">Analytics</Link>
            <Link to="/admin/billing" className="nav-link">Billing</Link>
            <Link to="/admin/settings" className="nav-link">Settings</Link>
          </nav>
        </div>
        <div className="admin-user-info">
          <span className="super-admin-badge">SUPER ADMIN</span>
          <span>{currentUser?.displayName || currentUser?.email}</span>
          <button onClick={handleLogout} className="logout-btn">Logout</button>
        </div>
      </header>

      <main className="admin-main">
        <Routes>
          <Route path="/dashboard" element={<AdminDashboard />} />
          <Route path="/institutions" element={<InstitutionsView />} />
          <Route path="/licenses" element={<LicensesView />} />
          <Route path="/users" element={<UsersView />} />
          <Route path="/analytics" element={<AnalyticsView />} />
          <Route path="/billing" element={<BillingView />} />
          <Route path="/settings" element={<SettingsView />} />
          <Route path="/" element={<Navigate to="/admin/dashboard" />} />
        </Routes>
      </main>
    </div>
  )
}

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalInstitutions: 0,
    loading: true
  })

  useEffect(() => {
    loadDashboardStats()
  }, [])

  const loadDashboardStats = async () => {
    try {
      // Fetch user count and institution stats in parallel
      const [usersResponse, institutionStats] = await Promise.all([
        api.auth.getUsers(),
        api.institution.getStats().catch(() => ({ total: 0 })) // Fallback if not super admin
      ])
      
      setStats({
        totalUsers: usersResponse.users.length,
        totalInstitutions: institutionStats.total || 0,
        loading: false
      })
    } catch (error) {
      console.error('Error loading dashboard stats:', error)
      setStats(prev => ({ ...prev, loading: false }))
    }
  }

  return (
    <div className="admin-dashboard">
      <h2>Super Admin Dashboard</h2>
      <div className="dashboard-stats">
        <div className="stat-card">
          <h3>Total Institutions</h3>
          <div className="stat-value">{stats.loading ? '...' : stats.totalInstitutions}</div>
          <div className="stat-label">Universities & Companies</div>
        </div>
        <div className="stat-card">
          <h3>Active Users</h3>
          <div className="stat-value">{stats.loading ? '...' : stats.totalUsers}</div>
          <div className="stat-label">From Firebase Auth</div>
        </div>
        <div className="stat-card">
          <h3>Monthly Revenue</h3>
          <div className="stat-value">$0</div>
          <div className="stat-label">License subscriptions</div>
        </div>
        <div className="stat-card">
          <h3>System Health</h3>
          <div className="stat-value">100%</div>
          <div className="stat-label">Uptime</div>
        </div>
      </div>
      
      <div className="dashboard-grid">
        <div className="dashboard-card">
          <h3>Institution Management</h3>
          <p>Add and manage universities and companies</p>
          <Link to="/admin/institutions" className="card-action">Manage Institutions</Link>
        </div>
        <div className="dashboard-card">
          <h3>License Management</h3>
          <p>Configure license tiers and pricing</p>
          <Link to="/admin/licenses" className="card-action">Manage Licenses</Link>
        </div>
        <div className="dashboard-card">
          <h3>User Management</h3>
          <p>View and assign roles to {stats.totalUsers} Firebase users</p>
          <Link to="/admin/users" className="card-action">Manage Users</Link>
        </div>
        <div className="dashboard-card">
          <h3>Global Analytics</h3>
          <p>Platform-wide usage and performance metrics</p>
          <Link to="/admin/analytics" className="card-action">View Analytics</Link>
        </div>
      </div>
    </div>
  )
}

const InstitutionsView = () => {
  const { refreshAvailableInstitutions } = useInstitution()
  const [institutions, setInstitutions] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [selectedInstitution, setSelectedInstitution] = useState<any | null>(null)
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 })

  useEffect(() => {
    loadInstitutions()
  }, [])

  const loadInstitutions = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await api.institution.getAll()
      setInstitutions(data)
      
      // Calculate stats
      const active = data.filter(inst => inst.is_active).length
      setStats({
        total: data.length,
        active: active,
        inactive: data.length - active
      })
    } catch (error) {
      console.error('Error loading institutions:', error)
      setError(error instanceof Error ? error.message : 'Failed to load institutions')
    } finally {
      setLoading(false)
    }
  }

  const handleAddInstitution = () => {
    setSelectedInstitution(null)
    setShowAddModal(true)
  }

  const handleEditInstitution = (institution: any) => {
    setSelectedInstitution(institution)
    setShowEditModal(true)
  }

  const handleDeleteClick = (institution: any) => {
    setSelectedInstitution(institution)
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = async () => {
    if (!selectedInstitution) return
    
    try {
      await api.institution.delete(selectedInstitution.id)
      setShowDeleteConfirm(false)
      setSelectedInstitution(null)
      loadInstitutions() // Refresh list
      refreshAvailableInstitutions() // Refresh available institutions for switcher
    } catch (error) {
      console.error('Error deleting institution:', error)
      setError(error instanceof Error ? error.message : 'Failed to delete institution')
    }
  }

  const handleModalSuccess = () => {
    setShowAddModal(false)
    setShowEditModal(false)
    setSelectedInstitution(null)
    loadInstitutions() // Refresh list
    refreshAvailableInstitutions() // Refresh available institutions for switcher
  }

  return (
  <div className="institutions-view">
    <div className="view-header">
        <div>
      <h2>Institution Management</h2>
          <div className="institutions-stats">
            <span>{stats.total} Total</span>
            <span>{stats.active} Active</span>
            <span>{stats.inactive} Inactive</span>
          </div>
        </div>
        <button className="primary-btn" onClick={handleAddInstitution}>
          Add New Institution
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={loadInstitutions} style={{ marginLeft: '10px' }}>Retry</button>
        </div>
      )}

      {loading ? (
        <div className="loading-message">Loading institutions...</div>
      ) : (
        <div className="institutions-table-container">
          <table className="institutions-table">
            <thead>
              <tr>
                <th>Institution</th>
                <th>Subdomain</th>
                <th>Theme</th>
                <th>Email Domains</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {institutions.map((institution) => (
                <tr key={institution.id}>
                  <td>
                    <div className="institution-info">
                      <strong>{institution.name}</strong>
                      <div className="institution-url">{institution.custom_url}</div>
                    </div>
                  </td>
                  <td>
                    <code className="subdomain-code">{institution.subdomain}</code>
                  </td>
                  <td>
                    <div className="theme-preview">
                      <div 
                        className="color-box" 
                        style={{ backgroundColor: institution.primary_color }}
                        title={`Primary: ${institution.primary_color}`}
                      ></div>
                      <div 
                        className="color-box" 
                        style={{ backgroundColor: institution.secondary_color }}
                        title={`Secondary: ${institution.secondary_color}`}
                      ></div>
                    </div>
                  </td>
                  <td>
                    <div className="email-domains">
                      {institution.allowed_email_domains.length > 0 ? (
                        institution.allowed_email_domains.map((domain: string, index: number) => (
                          <span key={index} className="domain-tag">{domain}</span>
                        ))
                      ) : (
                        <span className="no-domains">No domains</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${institution.is_active ? 'active' : 'inactive'}`}>
                      {institution.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="action-btn edit-btn"
                        onClick={() => handleEditInstitution(institution)}
                      >
                        Edit
                      </button>
                      <button 
                        className="action-btn delete-btn"
                        onClick={() => handleDeleteClick(institution)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {institutions.length === 0 && (
            <div className="empty-state">
              <h3>No institutions yet</h3>
              <p>Create your first institution to start managing white-label instances.</p>
              <button className="primary-btn" onClick={handleAddInstitution}>
                Add First Institution
              </button>
            </div>
          )}
        </div>
      )}

      {/* Add Institution Modal */}
      {showAddModal && (
        <InstitutionModal
          institution={null}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleModalSuccess}
          title="Add New Institution"
        />
      )}

      {/* Edit Institution Modal */}
      {showEditModal && selectedInstitution && (
        <InstitutionModal
          institution={selectedInstitution}
          onClose={() => setShowEditModal(false)}
          onSuccess={handleModalSuccess}
          title="Edit Institution"
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedInstitution && (
        <DeleteConfirmModal
          institution={selectedInstitution}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  )
}

// Institution Modal Component for Add/Edit
const InstitutionModal = ({ 
  institution, 
  onClose, 
  onSuccess, 
  title 
}: {
  institution: any | null;
  onClose: () => void;
  onSuccess: () => void;
  title: string;
}) => {
  const [formData, setFormData] = useState({
    name: institution?.name || '',
    subdomain: institution?.subdomain || '',
    custom_url: institution?.custom_url || '',
    logo_url: institution?.logo_url || '',
    primary_color: institution?.primary_color || '#3b82f6',
    secondary_color: institution?.secondary_color || '#1e40af',
    allowed_email_domains: institution?.allowed_email_domains?.join(', ') || '',
    is_active: institution?.is_active ?? true,
    allow_signups: institution?.allow_signups ?? true
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(institution?.logo_url || null)
  const [uploadingLogo, setUploadingLogo] = useState(false)

  // Update custom_url when subdomain changes
  useEffect(() => {
    if (formData.subdomain && (!institution || !formData.custom_url)) {
      setFormData(prev => ({
        ...prev,
        custom_url: `${formData.subdomain}.maxel.ai`
      }))
    }
  }, [formData.subdomain, institution])

  // Handle logo file selection
  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']
      if (!allowedTypes.includes(file.type)) {
        setError('Invalid file type. Please select a JPEG, PNG, GIF, WebP, or SVG image.')
        return
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size too large. Maximum size is 5MB.')
        return
      }
      
      setLogoFile(file)
      setError(null)
      
      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  // Remove logo file
  const handleRemoveLogoFile = () => {
    setLogoFile(null)
    setLogoPreview(institution?.logo_url || null)
    setError(null)
    // Clear the file input
    const fileInput = document.getElementById('logo-file-input') as HTMLInputElement
    if (fileInput) {
      fileInput.value = ''
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      setSaving(true)
      setError(null)

      let logoUrl = formData.logo_url.trim()

      // Upload logo file if one is selected
      if (logoFile) {
        try {
          setUploadingLogo(true)
          const uploadResult = await api.institution.uploadLogo(logoFile)
          logoUrl = uploadResult.logo_url
          console.log('Logo uploaded successfully:', logoUrl)
        } catch (uploadError) {
          console.error('Error uploading logo:', uploadError)
          setError(`Failed to upload logo: ${uploadError instanceof Error ? uploadError.message : 'Unknown error'}`)
          return
        } finally {
          setUploadingLogo(false)
        }
      }

      // Parse email domains
      const emailDomains = formData.allowed_email_domains
        .split(',')
        .map((domain: string) => domain.trim())
        .filter((domain: string) => domain.length > 0)

      const institutionData = {
        name: formData.name,
        subdomain: formData.subdomain,
        custom_url: formData.custom_url,
        logo_url: logoUrl,
        primary_color: formData.primary_color,
        secondary_color: formData.secondary_color,
        allowed_email_domains: emailDomains,
        is_active: formData.is_active,
        allow_signups: formData.allow_signups
      }

      if (institution) {
        // Update existing institution
        await api.institution.update(institution.id, institutionData)
      } else {
        // Create new institution
        await api.institution.create(institutionData)
      }

      onSuccess()
    } catch (error) {
      console.error('Error saving institution:', error)
      setError(error instanceof Error ? error.message : 'Failed to save institution')
    } finally {
      setSaving(false)
      setUploadingLogo(false)
    }
  }

  const handleChange = (field: string, value: any) => {
    // Validate and format hex colors
    if (field === 'primary_color' || field === 'secondary_color') {
      let colorValue = value;
      
      // If it's a hex input, validate and format
      if (typeof colorValue === 'string' && colorValue.length > 0) {
        // Remove any non-hex characters except #
        colorValue = colorValue.replace(/[^#0-9A-Fa-f]/g, '');
        
        // Ensure it starts with #
        if (!colorValue.startsWith('#')) {
          colorValue = '#' + colorValue;
        }
        
        // Validate hex format (3 or 6 characters after #)
        const hexMatch = colorValue.match(/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/);
        if (hexMatch) {
          // Convert 3-character hex to 6-character
          if (hexMatch[1].length === 3) {
            const shortHex = hexMatch[1];
            colorValue = '#' + shortHex.split('').map((char: string) => char + char).join('');
          }
        } else if (colorValue !== '#') {
          // If invalid and not just starting with #, don't update
          return;
        }
      }
      
      setFormData(prev => ({ ...prev, [field]: colorValue }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-body">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label>Institution Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Universidad Javeriana"
              required
            />
          </div>

          <div className="form-group">
            <label>Subdomain *</label>
            <input
              type="text"
              value={formData.subdomain}
              onChange={(e) => handleChange('subdomain', e.target.value.toLowerCase())}
              placeholder="javeriana"
              title="Lowercase letters, numbers, and hyphens. Must start and end with alphanumeric character."
              required
            />
            <small>Used for: {formData.subdomain || 'subdomain'}.maxel.ai</small>
          </div>

          <div className="form-group">
            <label>Custom URL</label>
            <input
              type="text"
              value={formData.custom_url}
              onChange={(e) => handleChange('custom_url', e.target.value)}
              placeholder="javeriana.maxel.ai"
            />
          </div>

          <div className="form-group">
            <label>Institution Logo</label>
            <div className="logo-upload-section">
              {logoPreview && (
                <div className="logo-preview">
                  <img src={logoPreview} alt="Logo preview" className="logo-preview-image" />
                  <button
                    type="button"
                    onClick={handleRemoveLogoFile}
                    className="remove-logo-btn"
                    title="Remove logo"
                  >
                    ×
                  </button>
                </div>
              )}
              <div className="logo-upload-input">
                <input
                  id="logo-file-input"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml"
                  onChange={handleLogoFileChange}
                  className="file-input-hidden"
                />
                <label htmlFor="logo-file-input" className="file-upload-btn">
                  {logoFile ? 'Change Logo' : logoPreview ? 'Replace Logo' : 'Choose Logo File'}
                </label>
                <small>Supports JPEG, PNG, GIF, WebP, SVG. Max 5MB.</small>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Primary Color</label>
              <div className="color-input-group">
                <input
                  type="color"
                  value={formData.primary_color}
                  onChange={(e) => handleChange('primary_color', e.target.value)}
                  className="color-picker"
                />
                <input
                  type="text"
                  value={formData.primary_color}
                  onChange={(e) => handleChange('primary_color', e.target.value)}
                  placeholder="#3b82f6"
                  pattern="^#[0-9A-Fa-f]{6}$"
                  title="Enter a valid hex color code (e.g., #3b82f6)"
                  className="hex-input"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Secondary Color</label>
              <div className="color-input-group">
                <input
                  type="color"
                  value={formData.secondary_color}
                  onChange={(e) => handleChange('secondary_color', e.target.value)}
                  className="color-picker"
                />
                <input
                  type="text"
                  value={formData.secondary_color}
                  onChange={(e) => handleChange('secondary_color', e.target.value)}
                  placeholder="#1e40af"
                  pattern="^#[0-9A-Fa-f]{6}$"
                  title="Enter a valid hex color code (e.g., #1e40af)"
                  className="hex-input"
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Allowed Email Domains</label>
            <input
              type="text"
              value={formData.allowed_email_domains}
              onChange={(e) => handleChange('allowed_email_domains', e.target.value)}
              placeholder="javeriana.edu.co, puj.edu.co, * (for any domain)"
            />
            <small>Comma-separated list of email domains for auto-assignment. Use * to allow any domain.</small>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => handleChange('is_active', e.target.checked)}
              />
              Active Institution
            </label>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.allow_signups}
                onChange={(e) => handleChange('allow_signups', e.target.checked)}
              />
              Allow New Signups
            </label>
            <small>When enabled, users can create new accounts on this institution's login page</small>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} disabled={saving || uploadingLogo}>
              Cancel
            </button>
            <button type="submit" className="primary-btn" disabled={saving || uploadingLogo}>
              {uploadingLogo ? 'Uploading Logo...' : saving ? 'Saving...' : (institution ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Delete Confirmation Modal
const DeleteConfirmModal = ({ 
  institution, 
  onClose, 
  onConfirm 
}: {
  institution: any;
  onClose: () => void;
  onConfirm: () => void;
}) => {
  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>Delete Institution</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        
        <div className="modal-body">
          <p>Are you sure you want to delete <strong>{institution.name}</strong>?</p>
          <p>This action cannot be undone. All users assigned to this institution will lose their institutional context.</p>
          
          <div className="institution-details">
            <div><strong>Subdomain:</strong> {institution.subdomain}</div>
            <div><strong>URL:</strong> {institution.custom_url}</div>
            <div><strong>Email Domains:</strong> {institution.allowed_email_domains.join(', ') || 'None'}</div>
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose}>Cancel</button>
          <button onClick={onConfirm} className="primary-btn delete-confirm">
            Delete Institution
          </button>
        </div>
    </div>
  </div>
)
}

const LicenseEditForm = ({ license, onSave, onCancel }: { 
  license: any; 
  onSave: (updates: any) => void; 
  onCancel: () => void; 
}) => {
  const [formData, setFormData] = useState({
    name: license.name,
    description: license.description,
    max_users: license.features.max_users,
    hours_per_user_monthly: license.features.hours_per_user_monthly || '',
    price_usd_monthly: license.features.price_usd_monthly,
    price_usd_yearly: license.features.price_usd_yearly || '',
    is_popular: license.is_popular
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    const updates = {
      name: formData.name,
      description: formData.description,
      is_popular: formData.is_popular,
      features: {
        ...license.features,
        max_users: parseInt(formData.max_users.toString()),
        hours_per_user_monthly: formData.hours_per_user_monthly ? parseInt(formData.hours_per_user_monthly.toString()) : null,
        price_usd_monthly: parseFloat(formData.price_usd_monthly.toString()),
        price_usd_yearly: formData.price_usd_yearly ? parseFloat(formData.price_usd_yearly.toString()) : null
      }
    }
    
    onSave(updates)
  }

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  return (
    <form onSubmit={handleSubmit} className="license-edit-form">
      <div className="form-group">
        <label>License Name</label>
        <input 
          type="text" 
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          required
        />
      </div>

      <div className="form-group">
        <label>Description</label>
        <textarea 
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          rows={3}
          required
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Max Users</label>
          <input 
            type="number" 
            value={formData.max_users}
            onChange={(e) => handleChange('max_users', e.target.value)}
            min="1"
            required
          />
        </div>

        {license.billing_model === 'hours_based' && (
          <div className="form-group">
            <label>Hours per User (monthly)</label>
            <input 
              type="number" 
              value={formData.hours_per_user_monthly}
              onChange={(e) => handleChange('hours_per_user_monthly', e.target.value)}
              min="0"
              step="0.1"
            />
          </div>
        )}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Monthly Price (USD)</label>
          <input 
            type="number" 
            value={formData.price_usd_monthly}
            onChange={(e) => handleChange('price_usd_monthly', e.target.value)}
            min="0"
            step="0.01"
            required
          />
        </div>

        <div className="form-group">
          <label>Yearly Price (USD)</label>
          <input 
            type="number" 
            value={formData.price_usd_yearly}
            onChange={(e) => handleChange('price_usd_yearly', e.target.value)}
            min="0"
            step="0.01"
          />
        </div>
      </div>

      <div className="form-group">
        <label className="checkbox-label">
          <input 
            type="checkbox" 
            checked={formData.is_popular}
            onChange={(e) => handleChange('is_popular', e.target.checked)}
          />
          Mark as Most Popular
        </label>
      </div>

      <div className="form-actions">
        <button type="button" className="secondary-btn" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="primary-btn">
          Save Changes
        </button>
      </div>
    </form>
  )
}

const LicensesView = () => {
  const [licenses, setLicenses] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedLicense, setSelectedLicense] = useState<any | null>(null)
  const [summary, setSummary] = useState<any>(null)

  useEffect(() => {
    loadLicenses()
    loadSummary()
  }, [])

  const loadLicenses = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await api.licenses.getAll()
      setLicenses(data)
    } catch (error) {
      console.error('Error loading licenses:', error)
      setError(error instanceof Error ? error.message : 'Failed to load licenses')
    } finally {
      setLoading(false)
    }
  }

  const loadSummary = async () => {
    try {
      const data = await api.licenses.getSummary()
      setSummary(data.summary)
    } catch (error) {
      console.error('Error loading license summary:', error)
    }
  }

  const handleInitializeLicenses = async () => {
    if (!window.confirm('This will initialize the 8 default licenses in the database. Continue?')) {
      return
    }

    try {
      setLoading(true)
      setError(null)
      const result = await api.licenses.initialize()
      setSuccess(result.message)
      await loadLicenses()
      await loadSummary()
    } catch (error) {
      console.error('Error initializing licenses:', error)
      setError(error instanceof Error ? error.message : 'Failed to initialize licenses')
    } finally {
      setLoading(false)
    }
  }

  const handleEditLicense = (license: any) => {
    setSelectedLicense(license)
    setShowEditModal(true)
  }

  const handleUpdateLicense = async (updates: any) => {
    if (!selectedLicense) return

    try {
      setError(null)
      await api.licenses.update(selectedLicense.id, updates)
      setSuccess('License updated successfully')
      setShowEditModal(false)
      setSelectedLicense(null)
      await loadLicenses()
      await loadSummary()
    } catch (error) {
      console.error('Error updating license:', error)
      setError(error instanceof Error ? error.message : 'Failed to update license')
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount)
  }

  const getBillingModelColor = (billingModel: string) => {
    return billingModel === 'hours_based' ? '#4CAF50' : '#2196F3'
  }

  const getBillingModelText = (billingModel: string) => {
    return billingModel === 'hours_based' ? 'Hours-Based' : 'Pay-Per-Print'
  }

  return (
    <div className="licenses-view">
      <div className="view-header">
        <div>
          <h2>License Management</h2>
          {summary && (
            <div className="license-stats">
              <span>{summary.total_licenses} Total</span>
              <span>{summary.hours_based_licenses} Hours-Based</span>
              <span>{summary.pay_per_print_licenses} Pay-Per-Print</span>
              <span>{summary.active_licenses} Active</span>
            </div>
          )}
        </div>
        <div className="view-actions">
          <button 
            className="secondary-btn" 
            onClick={loadLicenses}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button 
            className="primary-btn"
            onClick={handleInitializeLicenses}
            disabled={loading}
          >
            Initialize Default Licenses
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: '10px' }}>Dismiss</button>
        </div>
      )}

      {success && (
        <div className="success-message">
          {success}
          <button onClick={() => setSuccess(null)} style={{ marginLeft: '10px' }}>Dismiss</button>
        </div>
      )}

      {loading ? (
        <div className="loading-message">Loading licenses...</div>
      ) : (
        <div className="licenses-grid">
          {licenses.map((license) => (
            <div key={license.id} className={`license-card ${license.is_popular ? 'popular' : ''}`}>
              {license.is_popular && <div className="popular-badge">MOST POPULAR</div>}
              
              <div className="license-header">
                <h3 className="license-name">{license.name}</h3>
                <span 
                  className="license-type"
                  style={{ backgroundColor: getBillingModelColor(license.billing_model) }}
                >
                  {getBillingModelText(license.billing_model)}
                </span>
              </div>

              <p className="license-description">{license.description}</p>
              <p className="license-audience">{license.target_audience}</p>

              <div className="license-features">
                <div className="feature-row">
                  <span>Max Users:</span>
                  <strong>{license.features.max_users}</strong>
                </div>
                {license.billing_model === 'hours_based' && license.features.hours_per_user_monthly && (
                  <div className="feature-row">
                    <span>Hours per User:</span>
                    <strong>{license.features.hours_per_user_monthly}/month</strong>
                  </div>
                )}
                <div className="feature-row">
                  <span>Monthly Price:</span>
                  <strong>{formatCurrency(license.features.price_usd_monthly)}</strong>
                </div>
                {license.features.price_usd_yearly && (
                  <div className="feature-row">
                    <span>Yearly Price:</span>
                    <strong>{formatCurrency(license.features.price_usd_yearly)}</strong>
                  </div>
                )}
                <div className="feature-row">
                  <span>Cost per User:</span>
                  <strong>{formatCurrency(license.features.price_usd_monthly / license.features.max_users)}/month</strong>
                </div>
              </div>

              <div className="license-actions">
                <button 
                  className="edit-license-btn"
                  onClick={() => handleEditLicense(license)}
                >
                  Edit License
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {licenses.length === 0 && !loading && (
        <div className="empty-state">
          <h3>No licenses configured</h3>
          <p>Initialize the default license system to get started.</p>
          <button className="primary-btn" onClick={handleInitializeLicenses}>
            Initialize Default Licenses
          </button>
        </div>
      )}

      {/* Edit License Modal */}
      {showEditModal && selectedLicense && (
        <div className="modal-overlay">
          <div className="modal-content license-modal">
            <div className="modal-header">
              <h3>Edit License: {selectedLicense.name}</h3>
              <button 
                className="close-btn"
                onClick={() => setShowEditModal(false)}
              >
                ×
              </button>
            </div>
            
            <LicenseEditForm 
              license={selectedLicense}
              onSave={handleUpdateLicense}
              onCancel={() => setShowEditModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  )
}

const UsersView = () => {
  const [users, setUsers] = useState<FirebaseUser[]>([])
  const [filteredUsers, setFilteredUsers] = useState<FirebaseUser[]>([])
  const [institutions, setInstitutions] = useState<{[key: string]: string}>({}) // ID -> Name mapping
  const [allInstitutions, setAllInstitutions] = useState<any[]>([]) // Full institution objects for dropdowns
  const [selectedInstitutionFilter, setSelectedInstitutionFilter] = useState<string>('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAssignRole, setShowAssignRole] = useState(false)
  const [selectedUser, setSelectedUser] = useState<FirebaseUser | null>(null)
  const [assignRoleEmail, setAssignRoleEmail] = useState('')
  const [assignRoleRole, setAssignRoleRole] = useState('student')
  const [assignInstitutionId, setAssignInstitutionId] = useState<string>('') // New state for institution assignment
  const [assigning, setAssigning] = useState(false)
  const [assignSuccess, setAssignSuccess] = useState<string | null>(null)
  
  // Hardcoded super admin UID (must match backend)
  const SUPER_ADMIN_UID = "5T5YD9tSJKSIdRruHuLRyzUKmOr2"

  useEffect(() => {
    loadUsers()
  }, [])

  // Filter users when institution filter or users list changes
  useEffect(() => {
    if (selectedInstitutionFilter === 'all') {
      setFilteredUsers(users)
    } else if (selectedInstitutionFilter === 'unassigned') {
      setFilteredUsers(users.filter(user => !user.institution_id))
    } else {
      setFilteredUsers(users.filter(user => user.institution_id === selectedInstitutionFilter))
    }
  }, [users, selectedInstitutionFilter])

  const loadUsers = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Load users and institutions in parallel
      const [usersResponse, institutionsResponse] = await Promise.all([
        api.auth.getUsers(),
        api.institution.getAll()
      ])
      
      setUsers(usersResponse.users)
      setAllInstitutions(institutionsResponse) // Store full institution objects
      
      // Create institution ID -> name mapping
      const institutionMap: {[key: string]: string} = {}
      institutionsResponse.forEach((institution: any) => {
        institutionMap[institution.id] = institution.name
      })
      setInstitutions(institutionMap)
      
    } catch (err) {
      console.error('Error loading users:', err)
      setError('Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  const handleAssignRole = async () => {
    if (!assignRoleEmail || !assignRoleRole) return

    try {
      setAssigning(true)
      setError(null)
      setAssignSuccess(null)

      // Pass institution ID if selected (empty string = no institution = default platform)
      const institutionId = assignInstitutionId || undefined

      await api.auth.assignRoleByEmail(assignRoleEmail, assignRoleRole, institutionId)
      
      const institutionName = institutionId ? institutions[institutionId] || 'Unknown Institution' : 'Default Platform'
      setAssignSuccess(`Role ${assignRoleRole} assigned to ${assignRoleEmail} at ${institutionName} successfully!`)
      
      // Reset form
      setAssignRoleEmail('')
      setAssignRoleRole('student')
      setAssignInstitutionId('')
      setShowAssignRole(false)
      
      // Refresh users list
      await loadUsers()
    } catch (err) {
      console.error('Error assigning role:', err)
      setError(err instanceof Error ? err.message : 'Failed to assign role')
    } finally {
      setAssigning(false)
    }
  }

  const handleBatchAssign = async () => {
    if (!window.confirm('This will automatically assign ALL users to institutions based on their email domains. Users with matching email domains will be assigned to their respective institutions, others will remain on the default Maxel platform. Are you sure?')) {
      return
    }

    try {
      setAssigning(true)
      setError(null)
      setAssignSuccess(null)

      const result = await api.auth.batchAssignUsers()
      
      setAssignSuccess(
        `✅ Batch assignment completed! ` +
        `Processed: ${result.results.processed}, ` +
        `Assigned to institutions: ${result.results.assigned}, ` +
        `Assigned to default platform: ${result.results.default_assigned}, ` +
        `Already assigned: ${result.results.already_assigned}`
      )
      
      // Refresh users list
      await loadUsers()
    } catch (err) {
      console.error('Error in batch assignment:', err)
      setError(err instanceof Error ? err.message : 'Failed to perform batch assignment')
    } finally {
      setAssigning(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'role-badge role-super-admin'
      case 'institution_admin':
        return 'role-badge role-institution-admin'
      case 'teacher':
        return 'role-badge role-teacher'
      case 'student':
      default:
        return 'role-badge role-student'
    }
  }
  
  const getUserRoleDisplay = (user: any) => {
    // Ensure super admin always shows correctly regardless of backend response
    if (user.firebase_uid === SUPER_ADMIN_UID) {
      return 'SUPER ADMIN'
    }
    return user.role.replace('_', ' ').toUpperCase()
  }
  
  const getUserRoleBadgeClass = (user: any) => {
    // Ensure super admin always gets the correct badge class
    if (user.firebase_uid === SUPER_ADMIN_UID) {
      return 'role-badge role-super-admin'
    }
    return getRoleBadgeClass(user.role)
  }

  const getInstitutionName = (institutionId: string | undefined) => {
    if (!institutionId) return 'Default'
    return institutions[institutionId] || institutionId // Fallback to ID if name not found
  }

  return (
    <div className="users-view">
      <div className="view-header">
        <h2>User Management</h2>
        <div className="view-actions">
          <button 
            className="secondary-btn" 
            onClick={loadUsers}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button 
            className="primary-btn"
            onClick={() => setShowAssignRole(true)}
          >
            Assign Role
          </button>
          <button 
            className="batch-assign-btn"
            onClick={handleBatchAssign}
            disabled={loading || assigning}
          >
            {assigning ? 'Assigning...' : 'Auto-Assign All'}
          </button>
        </div>
      </div>

      {/* Institution Filter */}
      <div className="filter-section">
        <div className="filter-group">
          <label htmlFor="institution-filter">Filter by Institution:</label>
          <select
            id="institution-filter"
            value={selectedInstitutionFilter}
            onChange={(e) => setSelectedInstitutionFilter(e.target.value)}
            className="institution-filter-select"
          >
            <option value="all">All Institutions ({users.length} users)</option>
            <option value="unassigned">Default Platform ({users.filter(u => !u.institution_id).length} users)</option>
            {Object.entries(institutions).map(([id, name]) => {
              const count = users.filter(u => u.institution_id === id).length;
              return (
                <option key={id} value={id}>
                  {name} ({count} users)
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {assignSuccess && (
        <div className="success-message">
          {assignSuccess}
        </div>
      )}

      {loading ? (
        <div className="loading-message">Loading users from Firebase...</div>
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Display Name</th>
                <th>Role</th>
                <th>Institution</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.firebase_uid}>
                  <td className="user-email">{user.email}</td>
                  <td>{user.display_name || '-'}</td>
                  <td>
                    <span className={getUserRoleBadgeClass(user)}>
                      {getUserRoleDisplay(user)}
                    </span>
                  </td>
                  <td>{getInstitutionName(user.institution_id)}</td>
                  <td>{formatDate(user.created_at)}</td>
                  <td>
                    {user.firebase_uid === SUPER_ADMIN_UID ? (
                      <span className="super-admin-label">Super Admin</span>
                    ) : (
                      <button 
                        className="action-btn"
                        onClick={() => {
                          setSelectedUser(user)
                          setAssignRoleEmail(user.email)
                          setAssignRoleRole(user.role)
                          setAssignInstitutionId(user.institution_id || '') // Set institution ID for editing
                          setShowAssignRole(true)
                        }}
                      >
                        Edit Role
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredUsers.length === 0 && users.length > 0 && (
            <div className="no-users-message">
              No users found for the selected filter.
            </div>
          )}
          
          {users.length === 0 && (
            <div className="no-users-message">
              No users found. Users will appear here when they sign up.
            </div>
          )}
        </div>
      )}

      {/* Assign Role Modal */}
      {showAssignRole && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{selectedUser ? 'Edit User Role' : 'Assign Role to User'}</h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowAssignRole(false)
                  setSelectedUser(null)
                  setAssignRoleEmail('')
                  setAssignRoleRole('student')
                  setAssignInstitutionId('') // Reset institution ID
                  setError(null)
                  setAssignSuccess(null)
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Email Address:</label>
                <input
                  type="email"
                  value={assignRoleEmail}
                  onChange={(e) => setAssignRoleEmail(e.target.value)}
                  placeholder="user@example.com"
                  disabled={!!selectedUser}
                />
              </div>
              <div className="form-group">
                <label>Role:</label>
                <select
                  value={assignRoleRole}
                  onChange={(e) => setAssignRoleRole(e.target.value)}
                >
                  <option value="student">Student</option>
                  <option value="teacher">Teacher</option>
                  <option value="institution_admin">Institution Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label>Assign to Institution:</label>
                <select
                  value={assignInstitutionId}
                  onChange={(e) => setAssignInstitutionId(e.target.value)}
                  className="institution-select"
                >
                  <option value="">Default Platform</option>
                  {allInstitutions.map((inst) => (
                    <option key={inst.id} value={inst.id}>
                      {inst.name}
                    </option>
                  ))}
                </select>
              </div>
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="secondary-btn"
                onClick={() => {
                  setShowAssignRole(false)
                  setSelectedUser(null)
                  setAssignRoleEmail('')
                  setAssignRoleRole('student')
                  setAssignInstitutionId('') // Reset institution ID
                  setError(null)
                  setAssignSuccess(null)
                }}
              >
                Cancel
              </button>
              <button 
                className="primary-btn"
                onClick={handleAssignRole}
                disabled={assigning || !assignRoleEmail || !assignRoleRole}
              >
                {assigning ? 'Assigning...' : 'Assign Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="users-stats">
        <p>Showing: <strong>{filteredUsers.length}</strong> users</p>
        <p>Total Users: <strong>{users.length}</strong></p>
        {selectedInstitutionFilter !== 'all' && (
          <p>Filter: <strong>{selectedInstitutionFilter === 'unassigned' ? 'Default Platform Users' : institutions[selectedInstitutionFilter] || 'Unknown Institution'}</strong></p>
        )}
      </div>
    </div>
  )
}

const AnalyticsView = () => (
  <div className="analytics-view">
    <h2>Global Analytics</h2>
    <div className="coming-soon-large">
      <h3>Global Analytics Features:</h3>
      <ul>
        <li>Platform-wide usage statistics</li>
        <li>Cross-institution performance metrics</li>
        <li>Revenue and billing analytics</li>
        <li>System performance monitoring</li>
        <li>User engagement metrics</li>
      </ul>
      <p><strong>Status:</strong> Ready for implementation</p>
    </div>
  </div>
)

const BillingView = () => (
  <div className="billing-view">
    <h2>Billing Management</h2>
    <div className="coming-soon-large">
      <h3>Billing Management Features:</h3>
      <ul>
        <li>View all subscription revenues</li>
        <li>Manage payment methods</li>
        <li>Generate invoices and reports</li>
        <li>Handle billing disputes</li>
        <li>Configure payment settings</li>
      </ul>
      <p><strong>Status:</strong> Ready for implementation</p>
    </div>
  </div>
)

const SettingsView = () => (
  <div className="settings-view">
    <h2>System Settings</h2>
    <div className="coming-soon-large">
      <h3>System Settings Features:</h3>
      <ul>
        <li>Configure global platform settings</li>
        <li>Manage API keys and integrations</li>
        <li>Set system-wide limits and quotas</li>
        <li>Configure notification settings</li>
        <li>Manage platform branding</li>
      </ul>
      <p><strong>Status:</strong> Ready for implementation</p>
    </div>
  </div>
)

export default AdminApp 